/**
 * Statistics Hooks
 *
 * React Query hooks for the statistics system.
 * Exports both queryOptions (for SSR loaders) and hooks (for components).
 */

import { queryOptions, useQuery } from "@tanstack/react-query";
import { orpc } from "@/orpc/client";

// ============================================================================
// Types (inferred from API response)
// ============================================================================

export type StatisticsLeaderboardResult = Awaited<
  ReturnType<typeof orpc.f1.getStatisticsLeaderboard.call>
>;

export type DriverProfileResult = Awaited<
  ReturnType<typeof orpc.f1.getDriverProfile.call>
>;

export type ConstructorProfileResult = Awaited<
  ReturnType<typeof orpc.f1.getConstructorProfile.call>
>;

// ============================================================================
// Query Options (used in SSR loaders + hooks)
// ============================================================================

export const statisticsLeaderboardQueryOptions = (
  tab: "race_results" | "fantasy_stats",
  entity: "drivers" | "constructors",
  season: number,
) =>
  queryOptions({
    queryKey: ["f1", "statisticsLeaderboard", tab, entity, season] as const,
    queryFn: () =>
      orpc.f1.getStatisticsLeaderboard.call({ tab, entity, season }),
    staleTime: 1000 * 60 * 5,
  });

export const driverProfileQueryOptions = (slug: string, season: number) =>
  queryOptions({
    queryKey: ["f1", "driverProfile", slug, season] as const,
    queryFn: () => orpc.f1.getDriverProfile.call({ driverSlug: slug, season }),
    staleTime: 1000 * 60 * 5,
  });

export const constructorProfileQueryOptions = (slug: string, season: number) =>
  queryOptions({
    queryKey: ["f1", "constructorProfile", slug, season] as const,
    queryFn: () =>
      orpc.f1.getConstructorProfile.call({ constructorSlug: slug, season }),
    staleTime: 1000 * 60 * 5,
  });

// ============================================================================
// Hooks (used in components)
// ============================================================================

export function useStatisticsLeaderboard(
  tab: "race_results" | "fantasy_stats",
  entity: "drivers" | "constructors",
  season: number | undefined,
) {
  return useQuery({
    ...statisticsLeaderboardQueryOptions(tab, entity, season ?? 0),
    enabled: !!season,
  });
}

export function useDriverProfile(
  slug: string | undefined,
  season: number | undefined,
) {
  return useQuery({
    ...driverProfileQueryOptions(slug ?? "", season ?? 0),
    enabled: !!slug && !!season,
  });
}

export function useConstructorProfile(
  slug: string | undefined,
  season: number | undefined,
) {
  return useQuery({
    ...constructorProfileQueryOptions(slug ?? "", season ?? 0),
    enabled: !!slug && !!season,
  });
}
